import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Mail, ShieldCheck, AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import logger from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { login, loginWithGoogle } = useAuth();
  const { t, i18n } = useTranslation();
  const isFr = i18n.language === 'fr';

  // Step: 'credentials' | '2fa' | 'forgot' | 'sent'
  const [step, setStep] = useState('credentials');
  const [loading, setLoading] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [totpCode, setTotpCode] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');

  // Clear any stale pendingSignupRole left over from an aborted Google signup flow.
  // A user landing on /login is signing in, not signing up — the role flag must not
  // leak into a subsequent OAuth callback.
  useEffect(() => {
    sessionStorage.removeItem('pendingSignupRole');
  }, []);

  // Surface email-verification confirmation when redirected from /auth/verify-email/{token}.
  // Strips the query param after showing the toast so a refresh doesn't re-fire it.
  useEffect(() => {
    const verified = searchParams.get('verified');
    if (verified === 'true') {
      toast.success(isFr ? 'Courriel vérifié! Connectez-vous pour continuer.' : 'Email verified! Sign in to continue.');
    } else if (verified === 'already') {
      toast.success(isFr ? 'Votre courriel est déjà vérifié. Connectez-vous pour continuer.' : 'Your email is already verified. Sign in to continue.');
    }
    if (verified) {
      const next = new URLSearchParams(searchParams);
      next.delete('verified');
      setSearchParams(next, { replace: true });
    }
    // Mount-only: read the verified query param once on initial render. Re-running on
    // searchParams change would re-fire the toast after we clear the param.
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await login(formData.email, formData.password, null, rememberMe);
      if (result?.requires_2fa) {
        setStep('2fa');
        return;
      }
      if (result?.requires_email_verification) {
        toast.success(
          isFr
            ? 'Veuillez vérifier votre courriel avant de vous connecter.'
            : 'Please verify your email before signing in.'
        );
        setTimeout(() => navigate('/verify-email-sent', { replace: true }), 100);
        return;
      }
      toast.success(isFr ? 'Bienvenue!' : 'Welcome back!');
      const from = location.state?.from?.pathname || (result?.role === 'admin' ? '/admin' : '/dashboard');
      setTimeout(() => navigate(from, { replace: true }), 100);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      if (status === 423) {
        setError(detail || (isFr ? 'Compte verrouillé temporairement.' : 'Account temporarily locked. Please try again later.'));
      } else {
        setError(isFr ? 'Email ou mot de passe invalide.' : 'Invalid email or password.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result2fa = await login(formData.email, formData.password, totpCode, rememberMe);
      if (result2fa?.requires_email_verification) {
        toast.success(
          isFr
            ? 'Veuillez vérifier votre courriel avant de vous connecter.'
            : 'Please verify your email before signing in.'
        );
        setTimeout(() => navigate('/verify-email-sent', { replace: true }), 100);
        return;
      }
      toast.success(isFr ? 'Bienvenue!' : 'Welcome back!');
      const from = location.state?.from?.pathname || (result2fa?.role === 'admin' ? '/admin' : '/dashboard');
      setTimeout(() => navigate(from, { replace: true }), 100);
    } catch (err) {
      setError(err.response?.data?.detail || (isFr ? 'Code invalide.' : 'Invalid 2FA code.'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail, lang: i18n.language });
    } catch (err) {
      // Always show success to prevent email enumeration; log for debugging
      logger.error('Forgot password request error:', err);
    } finally {
      setForgotLoading(false);
      setStep('sent');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary to-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">

          {/* Logo */}
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">P</span>
            </div>
            <span className="font-bold text-gray-900 text-lg">ProposAI</span>
          </div>

          {/* Step: Credentials */}
          {step === 'credentials' && (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">{isFr ? 'Connexion' : 'Welcome back'}</h1>
              <p className="text-gray-500 text-sm mb-6">{isFr ? 'Connectez-vous à votre compte ProposAI' : 'Sign in to your ProposAI account'}</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">{isFr ? 'Courriel' : 'Email'}</Label>
                  <Input id="email" type="email" data-testid="login-email-input" value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="mt-1" autoFocus />
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <Label htmlFor="password">{isFr ? 'Mot de passe' : 'Password'}</Label>
                    <button type="button" onClick={() => { setForgotEmail(formData.email); setStep('forgot'); setError(''); }}
                      className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                      {isFr ? 'Mot de passe oublié?' : 'Forgot password?'}
                    </button>
                  </div>
                  <div className="relative">
                    <Input id="password" type={showPassword ? 'text' : 'password'} data-testid="login-password-input"
                      value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required className="pr-10" />
                    <button type="button" onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition" tabIndex={-1} data-testid="toggle-login-password">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {/* Remember me */}
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}
                    className="rounded border-gray-300 text-primary" data-testid="remember-me-checkbox" />
                  <span className="text-sm text-gray-600">{isFr ? 'Se souvenir de moi' : 'Remember me'}</span>
                </label>
                {/* Error */}
                {error && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5" data-testid="login-error">
                    <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading} data-testid="login-submit-btn">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />{isFr ? 'Connexion...' : 'Signing in...'}</> : (isFr ? 'Se connecter' : 'Sign In')}
                </Button>
              </form>

              {/* Google OAuth */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-muted-foreground">{isFr ? 'ou' : 'or'}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={async () => {
                  try { await loginWithGoogle(); }
                  catch { toast.error(t('auth.googleOAuthFailed')); }
                }}
                className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-border rounded-lg hover:bg-muted/50 transition-colors text-sm font-medium text-foreground"
                data-testid="google-login-btn"
              >
                <GoogleIcon />
                {t('landing.loginModal.googleSignIn')}
              </button>

              <p className="text-center text-sm text-gray-600 mt-6">
                {isFr ? 'Pas encore de compte?' : "Don't have an account?"}{' '}
                <Link to="/register" className="text-primary hover:underline font-medium" data-testid="login-register-link">
                  {isFr ? 'S\'inscrire' : 'Sign up'}
                </Link>
              </p>
            </>
          )}

          {/* Step: 2FA */}
          {step === '2fa' && (
            <div className="space-y-4">
              <div className="text-center py-2">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <ShieldCheck className="w-7 h-7 text-primary" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">{isFr ? 'Code de vérification' : 'Verification Code'}</h2>
                <p className="text-sm text-gray-500">{isFr ? 'Entrez le code à 6 chiffres de votre application.' : 'Enter the 6-digit code from your authenticator app.'}</p>
              </div>
              <form onSubmit={handle2FASubmit} className="space-y-4">
                <Input type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))} placeholder="000000"
                  className="text-center text-2xl tracking-widest font-mono" autoFocus required data-testid="totp-code-input" />
                {error && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                    <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" /><p className="text-sm text-red-700">{error}</p>
                  </div>
                )}
                <Button type="submit" disabled={loading || totpCode.length !== 6} className="w-full bg-primary hover:bg-primary/90" data-testid="totp-submit">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /></> : null}{isFr ? 'Vérifier' : 'Verify'}
                </Button>
              </form>
              <button onClick={() => { setStep('credentials'); setError(''); setTotpCode(''); }} className="w-full flex items-center justify-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition mt-2" data-testid="back-to-login">
                <ArrowLeft className="w-3.5 h-3.5" /> {isFr ? 'Retour' : 'Back to sign in'}
              </button>
            </div>
          )}

          {/* Step: Forgot Password */}
          {step === 'forgot' && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900">{t('auth.forgotPassword.resetTitle')}</h2>
              <p className="text-sm text-gray-500">{t('auth.forgotPassword.resetDescription')}</p>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label>{t('auth.forgotPassword.emailLabel')}</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required autoFocus
                      className="pl-9" placeholder={t('auth.forgotPassword.emailPlaceholder')} data-testid="forgot-email-input" />
                  </div>
                </div>
                <Button type="submit" disabled={forgotLoading} className="w-full bg-primary hover:bg-primary/90" data-testid="forgot-submit">
                  {forgotLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /></> : null}{t('auth.forgotPassword.sendResetLink')}
                </Button>
              </form>
              <button onClick={() => { setForgotLoading(false); setStep('credentials'); setError(''); }} className="w-full flex items-center justify-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition" data-testid="back-from-forgot">
                <ArrowLeft className="w-3.5 h-3.5" /> {t('auth.forgotPassword.backToSignIn')}
              </button>
            </div>
          )}

          {/* Step: Email Sent */}
          {step === 'sent' && (
            <div className="text-center py-4 space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">{t('auth.forgotPassword.emailSentCheck')}</h2>
                <p className="text-sm text-gray-500">
                  {t('auth.forgotPassword.emailSentDescription2', { email: forgotEmail })}
                </p>
              </div>
              <p className="text-xs text-gray-400">{t('auth.forgotPassword.checkSpam')}</p>
              <button onClick={() => { setStep('credentials'); setError(''); }} className="text-sm text-primary hover:underline font-medium" data-testid="back-to-login-from-sent">
                {t('auth.forgotPassword.backToSignIn')}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Login;
